'use client';

import { type RefObject, useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';

/** Small gap below the header bottom edge (px). */
const GAP_BELOW_HEADER_PX = 0;

function getTopBelowHeader(triggerEl: HTMLElement): number {
  const header = triggerEl.closest('header');
  const bottom = header
    ? header.getBoundingClientRect().bottom
    : triggerEl.getBoundingClientRect().bottom;
  return bottom + GAP_BELOW_HEADER_PX;
}

export type UseHeaderDropdownPositionOptions = {
  /**
   * When true, the menu’s **right** edge aligns with the trigger’s right edge (end of “Destinations”).
   * The panel grows **leftward**; `rightPx` is for CSS `right`, `anchorRightPx` caps width so it doesn’t overflow off the left.
   */
  alignMenuRightEdgeToTriggerRight?: boolean;
};

/**
 * Positions a fixed mega-menu flush under the site <header> (full height, incl. search row)
 * so it does not overlap the header. Uses fixed + horizontal insets elsewhere.
 */
export function useHeaderDropdownPosition(
  triggerRef: RefObject<HTMLElement | null>,
  options?: UseHeaderDropdownPositionOptions,
) {
  const [topPx, setTopPx] = useState<number | null>(null);
  /** CSS `right` (px from viewport right edge to menu’s right edge) */
  const [rightPx, setRightPx] = useState<number | null>(null);
  /** trigger.getBoundingClientRect().right — used to cap menu width (keep ≥1rem from viewport left) */
  const [anchorRightPx, setAnchorRightPx] = useState<number | null>(null);
  const alignMenuRight = options?.alignMenuRightEdgeToTriggerRight === true;

  const measure = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    setTopPx(getTopBelowHeader(el));
    if (alignMenuRight) {
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;
      setRightPx(vw - r.right);
      setAnchorRightPx(r.right);
    }
  }, [triggerRef, alignMenuRight]);

  const measureSync = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    flushSync(() => {
      setTopPx(getTopBelowHeader(el));
      if (alignMenuRight) {
        const r = el.getBoundingClientRect();
        const vw = window.innerWidth;
        setRightPx(vw - r.right);
        setAnchorRightPx(r.right);
      }
    });
  }, [triggerRef, alignMenuRight]);

  useEffect(() => {
    const onScrollOrResize = () => measure();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [measure]);

  return {
    topPx,
    rightPx: alignMenuRight ? rightPx : null,
    anchorRightPx: alignMenuRight ? anchorRightPx : null,
    measure,
    measureSync,
  };
}
